import { useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertCircle,
  Check,
  Loader2,
  Megaphone,
  Package,
  Pin,
  PinOff,
  Plus,
  ThumbsUp,
  Trash2,
  type LucideIcon,
} from '@/ui/icons'
import { SectionCard } from './SectionCard'
import { Button, Badge } from '@/ui/design-system'
import { Textarea } from '@/ui/components'
import { useTripFeed } from '@/modules/trip-feed'
import { canEditTrip } from '@/shared/utils/auth'
import { cn } from '@/shared/utils'
import { useTripDetailPanel } from '../contexts/TripDetailPanelContext'
import type {
  CreateTripFeedPostDto,
  MemberResponse,
  TripFeedPostType,
  TripResponse,
} from '@/shared/api/types'

interface TripFeedSectionProps {
  tripId: string
  trip: TripResponse & { members?: MemberResponse[] }
  user: { id: string } | null
}

const postTypeLabels: Record<TripFeedPostType, string> = {
  message: 'Update',
  offer: 'Offer',
  need: 'Need',
}

/** API/DB can still return removed types (e.g. `question`) until migrations ship. */
function normalizeTripFeedPostType(raw: string): TripFeedPostType {
  if (raw === 'message' || raw === 'offer' || raw === 'need') return raw
  return 'message'
}

interface TripFeedPostPresentation {
  Icon: LucideIcon
  rowClass: string
  badgeClass: string
  iconClass: string
  composeShellClass: string
}

const tripFeedPostPresentation: Record<TripFeedPostType, TripFeedPostPresentation> = {
  message: {
    Icon: Megaphone,
    rowClass:
      'border-earth-200 dark:border-earth-700 border-l-earth-400 dark:border-l-earth-500 bg-earth-50/60 dark:bg-earth-900/25',
    badgeClass:
      'border-earth-300/80 bg-earth-100 text-earth-800 dark:border-earth-600 dark:bg-earth-800/80 dark:text-earth-100',
    iconClass: 'text-earth-500 dark:text-earth-400',
    composeShellClass:
      'border-earth-200 dark:border-earth-700 border-l-earth-400 dark:border-l-earth-500 bg-earth-50/70 dark:bg-earth-900/35',
  },
  need: {
    Icon: AlertCircle,
    rowClass:
      'border-earth-200 dark:border-earth-700 border-l-warning bg-warning/[0.08] dark:bg-warning/[0.12]',
    badgeClass:
      'border-warning/35 bg-warning/15 text-warning dark:border-warning/40 dark:bg-warning/20 dark:text-warning-foreground',
    iconClass: 'text-warning',
    composeShellClass:
      'border-earth-200 dark:border-earth-700 border-l-warning bg-warning/[0.07] dark:bg-warning/[0.1]',
  },
  offer: {
    Icon: Package,
    rowClass:
      'border-earth-200 dark:border-earth-700 border-l-success bg-success/[0.08] dark:bg-success/[0.12]',
    badgeClass:
      'border-success/35 bg-success/15 text-success dark:border-success/40 dark:bg-success/20 dark:text-success-foreground',
    iconClass: 'text-success',
    composeShellClass:
      'border-earth-200 dark:border-earth-700 border-l-success bg-success/[0.07] dark:bg-success/[0.1]',
  },
}

const tripFeedRowBaseClass = 'rounded-md border border-l-4 p-3'

type FilterValue = 'all' | 'needs_offers'

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMins = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))
  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  return `${Math.floor(diffHours / 24)}d ago`
}

export function TripFeedSection({ tripId, trip, user }: TripFeedSectionProps) {
  const { selectedDetail, setSelectedDetail } = useTripDetailPanel()
  const wasTripFeedAddRef = useRef(false)
  const [activeFilter, setActiveFilter] = useState<FilterValue>('all')
  const canModerate = Boolean(user && canEditTrip(trip, user))
  const canPost = Boolean(user)
  const { posts, isLoading, error, refetch, deletePost, toggleReaction, togglePin } =
    useTripFeed(tripId)

  useEffect(() => {
    if (selectedDetail?.type === 'trip-feed-add') {
      wasTripFeedAddRef.current = true
      return
    }
    if (wasTripFeedAddRef.current) {
      refetch()
      wasTripFeedAddRef.current = false
    }
  }, [refetch, selectedDetail?.type])

  const filteredPosts = useMemo(() => {
    if (activeFilter === 'needs_offers') {
      return posts.filter((post) => post.post_type === 'need' || post.post_type === 'offer')
    }
    return posts
  }, [activeFilter, posts])

  return (
    <section id="trip-feed-section" className="min-w-0 flex-1 min-h-0 flex flex-col overflow-hidden">
      <SectionCard
        title="Trip Feed"
        count={posts.length}
        actions={
          canPost ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedDetail({ type: 'trip-feed-add' })}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Add
            </Button>
          ) : undefined
        }
        className="min-w-0 flex-1 min-h-0"
        contentClassName="pt-2"
        fixedHeight
      >
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {(['all', 'needs_offers'] as const).map((filter) => (
              <Button
                key={filter}
                variant={activeFilter === filter ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveFilter(filter)}
              >
                {filter === 'all' ? 'All' : 'Needs/Offers'}
              </Button>
            ))}
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-earth-500" />
            </div>
          ) : filteredPosts.length === 0 ? (
            <p className="text-sm text-earth-500 dark:text-muted-foreground">No feed posts yet.</p>
          ) : (
            <ul className="space-y-2">
              {filteredPosts.map((post) => {
                const canDelete = user && (post.author_id === user.id || canModerate)
                const displayPostType = normalizeTripFeedPostType(String(post.post_type))
                const presentation = tripFeedPostPresentation[displayPostType]
                const TypeIcon = presentation.Icon
                const thumbsUpCount = post.reactions.filter((r) => r.reaction_type === 'thumbs_up').length
                const checkCount = post.reactions.filter((r) => r.reaction_type === 'check').length

                return (
                  <li
                    key={post.id}
                    className={cn(tripFeedRowBaseClass, presentation.rowClass)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-2 text-xs text-earth-600 dark:text-muted-foreground">
                        <span className="inline-flex items-center gap-1.5">
                          <TypeIcon className={cn('h-3.5 w-3.5 shrink-0', presentation.iconClass)} aria-hidden />
                          <Badge variant="outline" className={presentation.badgeClass}>
                            {postTypeLabels[displayPostType]}
                          </Badge>
                        </span>
                        {post.is_pinned && <Badge variant="outline">Pinned</Badge>}
                        <span>{formatRelativeTime(post.created_at)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {canModerate && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => togglePin(post.id, !post.is_pinned)}
                            aria-label={post.is_pinned ? 'Unpin post' : 'Pin post'}
                          >
                            {post.is_pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deletePost(post.id)}
                            aria-label="Delete post"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <p className="mt-2 text-sm text-earth-800 dark:text-foreground whitespace-pre-wrap">
                      {post.content}
                    </p>
                    <div className="mt-3 flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleReaction(post.id, 'thumbs_up')}
                        className="gap-1"
                      >
                        <ThumbsUp className="h-3.5 w-3.5" /> {thumbsUpCount}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleReaction(post.id, 'check')}
                        className="gap-1"
                      >
                        <Check className="h-3.5 w-3.5" /> {checkCount}
                      </Button>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </SectionCard>
    </section>
  )
}

interface TripFeedComposeFormProps {
  onCreatePost: (payload: CreateTripFeedPostDto) => Promise<unknown>
  onSuccess?: () => void
}

export function TripFeedComposeForm({ onCreatePost, onSuccess }: TripFeedComposeFormProps) {
  const [postType, setPostType] = useState<TripFeedPostType>('message')
  const [content, setContent] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async () => {
    const trimmed = content.trim()
    if (!trimmed || isSubmitting) return
    setIsSubmitting(true)
    try {
      await onCreatePost({ postType, content: trimmed })
      setContent('')
      setPostType('message')
      onSuccess?.()
    } finally {
      setIsSubmitting(false)
    }
  }

  const composePresentation = tripFeedPostPresentation[postType]
  const ComposeTypeIcon = composePresentation.Icon

  return (
    <div
      className={cn(
        'rounded-md border border-l-4 p-3 space-y-2 transition-colors',
        composePresentation.composeShellClass
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <ComposeTypeIcon
          className={cn('h-4 w-4 shrink-0', composePresentation.iconClass)}
          aria-hidden
        />
        <span className="text-xs font-medium text-earth-700 dark:text-earth-200">
          New {postTypeLabels[postType].toLowerCase()}
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {(Object.keys(postTypeLabels) as TripFeedPostType[]).map((type) => {
          const { Icon: OptionIcon } = tripFeedPostPresentation[type]
          return (
            <Button
              key={type}
              variant={postType === type ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPostType(type)}
              disabled={isSubmitting}
              className="gap-1.5"
            >
              <OptionIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {postTypeLabels[type]}
            </Button>
          )
        })}
      </div>
      <Textarea
        rows={4}
        value={content}
        onChange={(event) => setContent(event.target.value)}
        placeholder="Share an update or coordinate what you need or are bringing..."
        disabled={isSubmitting}
      />
      <div className="flex justify-end">
        <Button onClick={handleSubmit} disabled={!content.trim() || isSubmitting}>
          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Post'}
        </Button>
      </div>
    </div>
  )
}
